/**
 * Step 3's plans — the village on the aged care spine (rebuild, 21 Oct 2026; the agreed mock-up's
 * j1-plans, j1-decided and j1-ap screens).
 *
 *  - Staying put as a strip of the four numbers from step 2 — every card is read against it. When the
 *    client has decided to move (step 1), the strip is "for reference only" and the cards are compared
 *    with each other instead: "The most of the ways to move", "−$X vs the best way to move".
 *  - One line saying the price of the move, in the client's own reasons ("the company and the less
 *    upkeep you're moving for"), and whether the Age Pension survives any way of moving.
 *  - "What matters most to you?" — the four goals; choosing one never re-runs the engine. "Highest Age
 *    Pension" with no way of moving keeping any opens one plain explanation in place of the cards.
 *  - The cards side by side, aligned row by row (owner, 20 Oct 2026): the plan in words, the estate,
 *    Age Pension / Left over each year / Village fees (decision 4, 21 Oct 2026), when the money runs
 *    out, an "Important" or "Worth knowing" line, and the three buttons.
 *  - The ways on: compare, every way tested, things that change the answer, build a plan yourself.
 *
 * Presentation only. Every figure is the engine row's own.
 */
import React from 'react';
import EnginePlanCard, { type PlanCardNumber } from '../agedCare/EnginePlanCard.tsx';
import EngineGoalControl, { type GoalFocusId } from '../agedCare/EngineGoalControl.tsx';
import DialogShell from '../../components/ui/DialogShell.tsx';
import { readMeansTestPosition, type MeansTestBreakdownRow } from '../../components/insights/MeansTestPosition.tsx';
import { formatCurrency } from '../../utils/index.ts';
import { householdVoice } from '../../utils/householdWords.ts';
import { planRunwayCopy } from '../../components/insights/fundingSummary.tsx';
import { runwayCardLine } from '../agedCare/moneyStory.tsx';
import { goalLeaderFor, NEAR_BEST_LABEL, type RecommendedStrategyEntry } from '../../strategy/supportAtHomeRecommendedStrategies.ts';
import { allPlansOpenLine, secondPlanLine, secondPlanChip, leadChip, leadLine, SECOND_PLAN_CHIP } from '../../strategy/planChoiceRule.ts';
import { CASHFLOW_COVERED_LABEL, coveredIsBestEstate } from '../../strategy/cashflowCoveredGoal.ts';
import { rvPlanWords } from './rvPlanWords.ts';
import { whyMoveWords, parseList } from './rvCopy.ts';
import { exitAfter } from './RvVillageCostsTile.tsx';
import { annuityHorizonWarningOf, annuityOfStrategy } from './RvStrategyCard.tsx';
import { rvGuaranteedIncomeOf, rvCompareFor, rvChoiceHeld } from './rvGuaranteedIncome.ts';
import AnnuityFamilyPanel from '../../components/strategy/AnnuityFamilyPanel.tsx';
import { IncomeOnlyBeforeChoose } from '../../components/strategy/IncomeOnlyTick.tsx';
import type { RvEvaluatedStrategy, VillageInputs } from './RetirementVillagePage.tsx';
import type { DraftFactFind } from '../../constants/initialFactFind.ts';
import type { FinancialPosition } from '../agedCare/FinancialImpactPanel.tsx';
import OtherGoalLeaders from '../../components/strategy/OtherGoalLeaders.tsx';
import { RENTED_HOME_CGT_CARD } from '../../strategy/strategyRisks.ts';
import SetAsideReveal from '../../components/strategy/SetAsideReveal.tsx';
import { benchmarkWords, householdRents } from '../../utils/benchmarkWords.ts';
import { ChosenAdviceLine } from '../../components/strategy/ChosenAdviceLine.tsx';

export interface RvSetAside { strategy: RvEvaluatedStrategy; labels: string[]; warning: string }

export interface RvPlansViewProps {
    factFind: DraftFactFind;
    strategies: RvEvaluatedStrategy[];
    stayHome: RvEvaluatedStrategy | null;
    recommended: RecommendedStrategyEntry[];
    setAside: RvSetAside[];
    villageInputs: VillageInputs;
    annualOngoing: number;
    horizonYears: number;
    /** What the household owns today (step 2's third number). */
    ownToday: number;
    focus: GoalFocusId;
    onFocus: (f: GoalFocusId) => void;
    chosenId: string | null;
    onChoose: (row: RvEvaluatedStrategy) => void;
    onExplain: (row: RvEvaluatedStrategy) => void;
    onRisks: (row: RvEvaluatedStrategy) => void;
    renderDetail: (row: RvEvaluatedStrategy) => React.ReactNode;
    onAllWays: () => void;
    onChanges: () => void;
    onBuildYourOwn: (row: RvEvaluatedStrategy | null) => void;
    onAdvice: () => void;
    onChangeDecided: () => void;
    /** Above everything: the chosen banner, or the note that the chosen plan is for another unit. */
    notices?: React.ReactNode;
    /** Records (or withdraws) the client's income-only confirmation for an amount (incomeOnlySetter). */
    onIncomeOnly?: (confirmed: boolean, amount: number) => void;
}

const GOAL_CHIP: Record<string, string> = {
    'Optimal Legacy': 'Leaves the most',
    [CASHFLOW_COVERED_LABEL]: 'Income covers the costs',
    'Optimal Cashflow': 'Most left over each year',
    'Optimal Age Pension': 'Highest Age Pension',
    [NEAR_BEST_LABEL]: SECOND_PLAN_CHIP,
};

const isMover = (s: RvEvaluatedStrategy) => s.id !== 'rv-stay-home' && !(s.inputs as Record<string, unknown> | undefined)?.stayHome;
const legacyOf = (s: RvEvaluatedStrategy | null | undefined) => Number(s?.snapshot?.legacyValue) || 0;
const pensionOf = (s: RvEvaluatedStrategy | null | undefined) => Number(s?.snapshot?.pension) || 0;
const cashOf = (s: RvEvaluatedStrategy | null | undefined) => Number(s?.snapshot?.netCashflow) || 0;
const money = (n: number) => formatCurrency(Math.round(n));
const signed = (n: number) => `${n >= 0 ? '+' : '−'}${money(Math.abs(n))}`;

/** Why a plan's Age Pension differs from staying put, in four words. */
function pensionReason(row: RvEvaluatedStrategy): string | null {
    const inputs = (row.inputs || {}) as Record<string, unknown>;
    if (inputs.rentHome === true) return 'the rented home is counted';
    if (inputs.fundFrom === 'sell_home') return 'the sale money is counted';
    if (inputs.fundFrom === 'investment_property') return 'the sale money is counted';
    return null;
}

const RvPlansView = ({
    factFind, strategies, stayHome, recommended, setAside, villageInputs, annualOngoing, horizonYears, ownToday, focus, onFocus,
    chosenId, onChoose, onExplain, onRisks, renderDetail, onAllWays, onChanges, onBuildYourOwn, onAdvice, onChangeDecided, notices, onIncomeOnly,
}: RvPlansViewProps) => {
    const v = householdVoice(factFind);
    const goals = (factFind?.goals || {}) as { rvDecided?: string; rvVillageName?: string; rvWhyMove?: string; rvWhyMoveOther?: string };
    const decided = goals.rvDecided === 'decided';
    const village = String(goals.rvVillageName || '').trim() || 'the village';
    const [openDetail, setOpenDetail] = React.useState<string | null>(null);
    const [why, setWhy] = React.useState<{ row: RvEvaluatedStrategy; page: 'pension' | 'cashflow' | 'fees' } | null>(null);
    const [comparing, setComparing] = React.useState(false);
    const yrs = `${horizonYears} ${horizonYears === 1 ? 'year' : 'years'}`;
    const entryFee = Number(villageInputs.entryFee) || 0;

    const movers = strategies.filter(isMover);
    const byName = (name?: string) => strategies.find((s) => s.name === name) ?? null;
    const fullRow = (r: RecommendedStrategyEntry | null): RvEvaluatedStrategy | null => (r?.strategy?.name ? (byName(r.strategy.name) || (r.strategy as RvEvaluatedStrategy)) : null);
    const coveredSame = coveredIsBestEstate(recommended);
    const effective: GoalFocusId = focus === 'covered' && coveredSame ? 'legacy' : focus;
    const base = stayHome?.snapshot ? stayHome : null;
    const bestMover = movers.length ? movers.reduce((top, s) => (legacyOf(s) > legacyOf(top) ? s : top), movers[0]!) : null;
    const noPensionAnyWay = movers.length > 0 && movers.every((s) => pensionOf(s) < 1);
    const pensionStops = noPensionAnyWay && pensionOf(base) >= 1;

    const leader = fullRow(goalLeaderFor(recommended, effective));
    const secondEntry = effective === 'legacy' ? recommended.find((r) => r.label === NEAR_BEST_LABEL && r.second) ?? null : null;
    const secondRow = secondEntry ? fullRow(secondEntry) : null;
    const estateEntry = recommended.find((r) => r.label === 'Optimal Legacy') ?? null;
    /*
     * ONE CARD PER GOAL, A SECOND ONLY WITH A PURPOSE (owner, 22 Oct 2026: "each section has a specific purpose … only
     * show the second card when there is a purpose to it"). The second card is the plan choice rule's near-tie beside
     * the estate leader (nearly the same estate, less locked away or a better cashflow — planChoiceRule.ts), as aged
     * care and support at home do. Another goal's leader is pointed to in one line under the cards (OtherGoalLeaders).
     */
    const showPensionNote = effective === 'pension' && noPensionAnyWay;
    const shown = showPensionNote || !leader ? [] : [leader, ...(secondRow && secondRow.name !== leader.name ? [secondRow] : [])];
    const others = (['legacy', 'cashflow', 'pension'] as const)
        .filter((g) => g !== effective)
        .map((g) => ({ g, row: fullRow(goalLeaderFor(recommended, g)) }))
        .filter((x) => !!x.row && !shown.some((s) => s.name === x.row!.name))
        .map((x) => ({ goal: x.g, title: rvPlanWords(x.row!, factFind, entryFee).title, legacy: legacyOf(x.row), cashflow: cashOf(x.row), pension: pensionOf(x.row) }));
    // “Income covers the costs” only on a plan that does: when none does, that goal falls back to the estate leader,
    // and the label on a plan that is short each year would contradict its own figures (review, 22 Oct 2026).
    const labelsFor = (row: RvEvaluatedStrategy): string[] => recommended.filter((r) => r.strategy?.name === row.name).map((r) => String(r.label))
        .filter((l) => l !== CASHFLOW_COVERED_LABEL || (Number(row.snapshot?.netCashflow) || 0) >= 0);
    const chipFor = (l: string): string | undefined => (l === 'Optimal Legacy' ? leadChip(estateEntry?.lead) : l === NEAR_BEST_LABEL && secondEntry?.second ? secondPlanChip(secondEntry.second) : GOAL_CHIP[l]);

    // What every card is measured against: staying put, or — decided to move — the best way to move.
    const against = decided ? bestMover : base;
    const againstWord = decided ? 'the best way to move' : benchmarkWords('village', { rents: householdRents(factFind as never) }).word;
    const maxGain = Math.max(1, ...shown.map((s) => Math.abs(legacyOf(s) - legacyOf(against))));
    const { dmf } = exitAfter(villageInputs, horizonYears);

    // The price of the move, in the client's reasons.
    const reasons = whyMoveWords(parseList((goals as { rvWhyMove?: string }).rvWhyMove), goals.rvWhyMoveOther);
    const cost = base && bestMover ? legacyOf(base) - legacyOf(bestMover) : 0;
    const homeValue = Number(factFind?.home?.value) || 0;
    const verdict = base && bestMover ? (
        cost > 0 ? (
            <>
                <strong>Moving to {village} costs {v.them} at least {money(cost)} over about {yrs}, against staying put.</strong>{' '}
                {reasons && <>That is the price of the {reasons} {v.you ? "you're" : `${v.they} ${v.are}`} moving for. </>}
                {homeValue > entryFee && entryFee > 0 && <>The family home is worth more than the unit{pensionStops ? ',' : '.'} </>}
                {pensionStops && <>{homeValue > entryFee && entryFee > 0 ? 'and' : ''} {v.possessive} Age Pension stops in every way of moving. </>}
                We tested {strategies.length} ways; below are the least costly, and the easiest to live on.
            </>
        ) : (
            <>
                <strong>Moving to {village} can leave the family {money(-cost)} more than staying put over about {yrs}</strong>, on the best way of doing it.{' '}
                {pensionStops && <>{v.Possessive} Age Pension stops in every way of moving. </>}
                We tested {strategies.length} ways; below are the strongest.
            </>
        )
    ) : null;

    const goalSubs: Partial<Record<GoalFocusId, string>> = {
        legacy: decided ? 'Of the ways to move' : cost > 0 ? 'Smallest cost against staying put' : `Biggest estate after about ${yrs}`,
        covered: coveredSame ? 'Same plan as "leave the most"' : 'Pays its way without savings',
        cashflow: 'Easiest cashflow',
        pension: noPensionAnyWay ? (effective === 'pension' ? 'No way of moving keeps any' : 'No way of moving keeps any — see why') : 'Most government support',
    };

    const card = (row: RvEvaluatedStrategy) => {
        const key = String(row.id ?? row.name);
        const words = rvPlanWords(row, factFind, entryFee);
        const chip = labelsFor(row).map(chipFor).filter(Boolean).join(' · ') || null;
        const delta = against ? legacyOf(row) - legacyOf(against) : null;
        const isBestOfMoves = decided && bestMover && row.name === bestMover.name;
        const pDelta = base ? pensionOf(row) - pensionOf(base) : 0;
        const cDelta = base ? cashOf(row) - cashOf(base) : 0;
        const reason = pensionReason(row);
        const numbers: PlanCardNumber[] = [
            {
                label: 'Age Pension', value: pensionOf(row), unit: 'a year',
                vs: base && Math.abs(pDelta) >= 1
                    ? <><span className={`font-bold ${pDelta > 0 ? 'text-emerald-700' : 'text-rose-700'}`}>{signed(pDelta)} a year</span>{reason && pDelta < 0 ? <> — {reason}</> : <> vs staying put</>}</>
                    : 'the same as staying put',
                tone: pDelta > 0 ? 'good' : 'neutral',
                onWhy: () => setWhy({ row, page: 'pension' }),
            },
            {
                label: cashOf(row) < 0 ? 'Each year, short by' : 'Left over each year', value: Math.abs(cashOf(row)),
                vs: base
                    ? <><span className={`font-bold ${cDelta >= 0 ? 'text-emerald-700' : 'text-rose-700'}`}>{signed(cDelta)} a year</span> vs staying put ({cashOf(base) < 0 ? `short ${money(-cashOf(base))}` : `${money(cashOf(base))} to spare`})</>
                    : '',
                tone: cashOf(row) >= 0 ? (cDelta > 0 ? 'good' : 'neutral') : 'neutral',
                onWhy: () => setWhy({ row, page: 'cashflow' }),
            },
            {
                label: 'Village fees', value: annualOngoing, unit: 'a year',
                vs: dmf > 0 ? <>departure fee {money(dmf)} if {v.they} left after {yrs}</> : 'no departure fee on this contract',
                onWhy: () => setWhy({ row, page: 'fees' }),
            },
        ];
        /*
         * WHEN THE MONEY RUNS OUT, AS EVERY MODULE'S CARD SAYS IT (owner, 22 Oct 2026): income covers it, or every
         * account in the order it is drawn and when each runs low (runwayCardLine) — the village's own wording, and
         * its bank-account-only flag, are gone. The one village fact it does not say is kept: a plan that covers its
         * costs with almost nothing to spare (said under "Worth knowing" instead once the family has decided).
         */
        const cashLeft = cashOf(row);
        const runText = row.projections && row.projections.length ? runwayCardLine({ rows: row.projections as never, factFind, horizonYears, spentOn: 'the entry payment' }) : null;
        const runway = !runText && row.projections && row.projections.length ? planRunwayCopy(row.projections as never, horizonYears) : null;
        const thin = !decided && cashLeft >= 0 && cashLeft < 2000 ? ` There is almost nothing to spare, though: ${money(cashLeft)} a year.` : '';
        const runline = runText
            ? <><strong className="text-slate-900">When the money runs out:</strong> {runText}{thin}</>
            : runway ? <><strong className="text-slate-900">When the money runs out:</strong> {runway.headline}{runway.flag ? ` ${runway.flag}` : ''}{thin}</> : null;
        const rented = (row.inputs as Record<string, unknown> | undefined)?.rentHome === true;
        const annuity = annuityOfStrategy(row, factFind);
        const horizonWarning = annuityHorizonWarningOf(row, factFind);
        const onlyWay = (row as { onlyWayToFund?: boolean }).onlyWayToFund === true
            || (row.inputs as { onlyWayToFund?: boolean } | undefined)?.onlyWayToFund === true;
        const lines: React.ReactNode[] = [];
        if (onlyWay) lines.push(<><strong className="text-slate-900">The only way to fund this unit.</strong> {v.you ? 'You said' : `${v.Names} said`} keep the home, but keeping it can&apos;t raise the {money(entryFee)}, so the plans that sell are shown.</>);
        const baseDelta = base ? legacyOf(row) - legacyOf(base) : 0;
        if (!decided && baseDelta < -1) {
            lines.push(<><strong className="text-rose-700">Important:</strong> the move costs the family <strong className="text-rose-700">{money(-baseDelta)}</strong> against staying put{pensionOf(row) < 1 && pensionOf(base) >= 1 ? ', and the Age Pension stops' : ''}.{rented ? ` It also depends on the rent and the costs being right — check land tax. ${RENTED_HOME_CGT_CARD}` : (row.inputs as { fundFrom?: string } | undefined)?.fundFrom === 'sell_home' ? ' Selling costs, the departure fee and any lost pension all come out of the estate.' : ''}</>);
        } else if (decided) {
            const parts: string[] = [];
            if (rented) parts.push('it depends on the rent and the costs being right, so check land tax', RENTED_HOME_CGT_CARD.replace(/\.$/, ''));
            if (cashLeft >= 0 && cashLeft < 2000) parts.push(`it leaves only ${money(cashLeft)} a year to spare`);
            if (!isBestOfMoves && delta != null && delta < -1 && cashLeft >= 2000) parts.push(`it gives ${money(cashLeft)} a year to live on, for ${money(-delta)} less to the family than the best way to move`);
            if (parts.length) lines.push(<><strong className="text-slate-900">Worth knowing:</strong> {parts.join('. ').replace(/^./, (c) => c.toUpperCase())}.</>);
        }
        if (annuity && Math.round(annuity.residualPercent) === 0) lines.push(<><strong className="text-slate-900">Worth knowing:</strong> the guaranteed income is income only. It pays the most for life, and nothing from it goes back to the family, even after an early death.</>);
        if (horizonWarning) lines.push(<>{horizonWarning}</>);
        const isChosen = chosenId != null && row.id === chosenId;
        /*
         * WHAT THE FAMILY RECEIVES, IF DEATH COMES EARLY (guaranteed income review E4, 21 Oct 2026): on every plan
         * with a guaranteed income, against the same sale with the money invested instead. And income only can't
         * be chosen until the client has confirmed it for this amount (E5).
         */
        const gi = rvGuaranteedIncomeOf(row, factFind);
        const compare = gi ? rvCompareFor(row, strategies) : null;
        const familyPanel = gi ? (
            <div className={runline ? 'mt-3' : ''}>
                <AnnuityFamilyPanel
                    factFind={factFind}
                    personName={gi.personName}
                    amount={gi.amount}
                    kind={gi.kind}
                    incomeAYear={gi.incomeAYear}
                    rows={row.projections as never}
                    compareRows={(compare?.row.projections as never) ?? null}
                    compareNote={compare?.note ?? null}
                    horizonYears={horizonYears}
                    testId="rv-annuity-family-panel"
                />
            </div>
        ) : null;
        const incomeOnly = !!gi && gi.kind === 'incomeOnly';
        const tradeOff =secondEntry?.second && row === secondRow ? secondPlanLine(secondEntry.second, formatCurrency) : estateEntry?.lead && row.name === estateEntry.strategy?.name ? leadLine(estateEntry.lead, formatCurrency) : null;
        return (
            <EnginePlanCard
                key={key}
                aligned
                testId="rv-plan-card"
                whyShown={tradeOff || null}
                chip={chip}
                chipTone="blue"
                title={words.title}
                sub={words.sub}
                legacy={legacyOf(row)}
                legacyDelta={delta}
                horizonYears={horizonYears}
                baselineWord={againstWord}
                deltaNote={isBestOfMoves ? 'The most of the ways to move' : undefined}
                bars={delta != null ? { gainPct: (Math.abs(delta) / maxGain) * 100, tiedUp: entryFee, tiedUpPct: 100 } : null}
                tiedUpLabel="Paid into the village"
                numbers={numbers}
                runline={runline || familyPanel ? <>{runline}{familyPanel}</> : null}
                worthKnowing={lines.length ? <>{lines.map((l, i) => <p key={i}>{l}</p>)}</> : null}
                lines={[]}
                chosen={isChosen}
                beforeChoose={incomeOnly && onIncomeOnly ? <IncomeOnlyBeforeChoose factFind={factFind} amount={gi!.amount} onChange={onIncomeOnly} /> : undefined}
                chooseHeld={incomeOnly && rvChoiceHeld(row, factFind)}
                onExplain={() => onExplain(row)}
                onRisks={() => onRisks(row)}
                onChoose={() => onChoose(row)}
                chooseLabel={chosenId != null && !isChosen ? 'Choose this instead' : 'Choose this plan'}
                chosenStrip={<ChosenAdviceLine possessiveFile={v.possessive} onAdvice={onAdvice} onCustomise={() => onBuildYourOwn(row)} />}
                detail={renderDetail(row)}
                detailOpen={openDetail === key}
                onToggleDetail={() => setOpenDetail((d) => (d === key ? null : key))}
                detailHint="where the money comes from and every calculation"
            />
        );
    };

    const Strip = () => (
        <div data-testid="rv-stay-put-strip" className={decided ? 'opacity-75' : ''}>
            <p className="text-[13px] font-semibold text-slate-500">{decided ? 'For reference only — staying put' : 'Compared with staying put — the four numbers from step 2'}</p>
            <div className="mt-2 grid grid-cols-2 gap-px overflow-hidden rounded-xl border border-slate-200 bg-slate-200 shadow-card lg:grid-cols-4">
                {[
                    { label: 'Age Pension', value: money(pensionOf(base)), note: 'a year' },
                    { label: cashOf(base) < 0 ? 'Short each year' : 'Left over each year', value: money(Math.abs(cashOf(base))), note: 'money in, less money out', tone: cashOf(base) >= 0 ? 'text-emerald-700' : 'text-rose-700' },
                    { label: `What ${v.you ? 'you' : v.they} own`, value: money(ownToday), note: 'today' },
                    { label: 'Left to the family', value: money(legacyOf(base)), note: `after about ${yrs}` },
                ].map((c) => (
                    <div key={c.label} className="bg-white px-4 py-3">
                        <p className="text-[12px] font-semibold text-slate-500">{c.label}</p>
                        <p className={`mt-0.5 font-bold tabular-nums leading-tight ${decided ? 'text-[18px]' : 'text-[22px]'} ${c.tone || 'text-slate-900'}`}>{c.value}</p>
                        <p className="text-[12px] text-slate-500">{c.note}</p>
                    </div>
                ))}
            </div>
        </div>
    );

    const waysOn = (
        <>
            <div className="flex flex-wrap items-center gap-3" data-testid="rv-ways-on">
                {shown.length > 0 && <button type="button" onClick={() => setComparing(true)} className="rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-[14px] font-semibold text-slate-800 hover:border-blue-300" data-testid="rv-compare">Compare the plans side by side</button>}
                <button type="button" onClick={onAllWays} className="rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-[14px] font-semibold text-slate-800 hover:border-blue-300" data-testid="rv-all-ways">All {strategies.length} ways tested</button>
                <button type="button" onClick={onChanges} className="rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-[14px] font-semibold text-slate-800 hover:border-blue-300" data-testid="rv-changes">Things that change the answer</button>
                <button type="button" onClick={() => onBuildYourOwn(null)} className="text-[14px] font-semibold text-brand-navy underline decoration-slate-300 underline-offset-4 hover:decoration-brand-navy" data-testid="rv-build-your-own">Prefer to set the numbers yourself? Build a plan →</button>
            </div>
            <p className="text-[13px] text-slate-500" data-testid="all-plans-open-line">{allPlansOpenLine(strategies.length)}</p>
            <p className="text-[13px] text-slate-500">Figures use the rates in force today and are projections over {yrs}, not guarantees.</p>
        </>
    );

    return (
        <div className="space-y-6" data-testid="rv-plans">
            {notices}
            {decided && verdict && (
                <div className="rounded-xl border border-slate-200 border-l-4 border-l-brand-navy bg-white px-4 py-3 text-[14px] leading-relaxed text-slate-700 shadow-card" data-testid="rv-decided-line">
                    <strong className="text-slate-900">{v.Names} {v.have} decided to move</strong>, so these are the best ways to do it, compared with each other.{' '}
                    {cost > 0 ? <>Moving costs {v.them} at least {money(cost)} over about {yrs} against staying put</> : <>The best way of moving leaves {money(-cost)} more than staying put</>}
                    {pensionStops ? <>, and {v.possessive} Age Pension stops</> : null}; staying put is shown below for reference.{' '}
                    <button type="button" onClick={onChangeDecided} className="font-semibold text-brand-navy underline decoration-slate-300 underline-offset-4">Still deciding? Change it on step 1</button>
                </div>
            )}

            {base && <Strip />}

            {!decided && verdict && (
                <div className={`rounded-xl border border-slate-200 border-l-4 bg-white px-4 py-3 text-[14px] leading-relaxed text-slate-700 shadow-card ${cost > 0 ? 'border-l-rose-500' : 'border-l-emerald-500'}`} data-testid="rv-verdict">
                    {verdict}
                </div>
            )}

            <EngineGoalControl
                value={focus}
                onChange={onFocus}
                household={v.names}
                enteringName={v.names}
                horizonYears={horizonYears}
                coveredSameAsBest={coveredSame}
                bestCovers={coveredSame && cashOf(fullRow(goalLeaderFor(recommended, 'legacy'))) >= 0}
                waysTested={strategies.length}
                shown={shown.length}
                testedFor="to fund the move"
                closingNote={<>Each card says when the savings would run out, and what the village fees and the departure fee are.</>}
                subs={goalSubs}
                countLine={showPensionNote ? 'No way of moving keeps any Age Pension.' : shown.length === 0 ? 'None leads on this goal.' : undefined}
            />

            {showPensionNote ? (
                <div className="rounded-2xl border border-slate-200 border-l-4 border-l-brand-navy bg-white p-6 shadow-card" data-testid="rv-no-pension">
                    <h3 className="text-[19px] font-bold text-slate-900">No way of moving keeps {v.possessive} Age Pension</h3>
                    <p className="mt-2 text-[15px] leading-relaxed text-slate-700">
                        {pensionOf(base) >= 1
                            ? <>Staying put, {v.they} get{v.s} <strong>{money(pensionOf(base))} a year</strong>. Once {v.they} move, Centrelink counts more of what {v.they} own than {v.couple ? 'a couple is' : 'one person is'} allowed, whichever way {v.they} pay for the unit:</>
                            : <>Staying put, Centrelink already counts more than {v.couple ? 'a couple is' : 'one person is'} allowed, and moving doesn&apos;t change that:</>}
                    </p>
                    <ul className="mt-2 list-disc space-y-1 pl-5 text-[15px] leading-relaxed text-slate-700">
                        {homeValue > 0 && <li><strong>If {v.they} keep the home and rent it out,</strong> the home counts in full, because it is no longer where {v.they} live, and the rent counts as income.</li>}
                        {homeValue > 0 && <li><strong>If {v.they} sell,</strong> the {money(Math.max(0, homeValue * 0.975 - entryFee))} left after the entry payment counts, wherever it is put.</li>}
                        {homeValue <= 0 && <li>The savings that are left after the entry payment, and any income from them, are counted.</li>}
                    </ul>
                    <p className="mt-3 text-[15px] leading-relaxed text-slate-700">So this goal can&apos;t tell the plans apart. Choose one of the other three goals to compare them.</p>
                    <div className="mt-4 flex flex-wrap gap-3">
                        {bestMover && <button type="button" onClick={() => setWhy({ row: bestMover, page: 'pension' })} className="rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-[14px] font-semibold text-slate-800 hover:border-blue-300">Why {v.possessive} Age Pension is $0</button>}
                        <button type="button" onClick={() => onFocus('legacy')} className="rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-[14px] font-semibold text-slate-800 hover:border-blue-300">Compare the plans on another goal</button>
                    </div>
                </div>
            ) : shown.length === 0 ? (
                <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-card" data-testid="rv-no-leader">
                    <h3 className="text-[19px] font-bold text-slate-900">No plan leads on this goal</h3>
                    <p className="mt-2 text-[14px] leading-relaxed text-slate-700">Choose another goal above, or see all {strategies.length} ways tested.</p>
                </div>
            ) : (
                <div className={`grid grid-cols-1 gap-5 ${shown.length > 1 ? 'lg:grid-cols-2' : ''}`} data-testid="rv-plan-cards">
                    {chosenId != null && !shown.some((s) => s.id === chosenId) && (
                        <div className={shown.length > 1 ? 'lg:col-span-2' : ''}><ChosenAdviceLine possessiveFile={v.possessive} onAdvice={onAdvice} offCard testId="chosen-plan-off-card" /></div>
                    )}
                    {shown.map(card)}
                </div>
            )}
            {shown.length > 0 && <OtherGoalLeaders others={others} here={{ legacy: legacyOf(shown[0]), cashflow: cashOf(shown[0]), pension: pensionOf(shown[0]) }} baseLegacy={base ? legacyOf(base) : null} baseWord="staying put" onFocus={onFocus} testId="rv-other-leaders" />}

            {/* "See it anyway" opens the plan's own card beneath the note (owner, 23 Oct 2026; SetAsideReveal). */}
            {setAside.map((sa) => (
                <SetAsideReveal
                    key={String(sa.strategy.id ?? sa.strategy.name)}
                    testId="rv-set-aside"
                    className="rounded-xl border border-slate-200 border-l-4 border-l-amber-400 bg-white px-4 py-3 text-[14px] leading-relaxed text-slate-700"
                    note={(link) => <p>{setAside.length === 1 ? 'One plan was set aside: ' : ''}{rvPlanWords(sa.strategy, factFind, entryFee).title.replace(/^./, (c) => c.toLowerCase())} would lead on {sa.labels.map((l) => ({ 'Optimal Legacy': 'the estate', 'Optimal Cashflow': 'cashflow', 'Optimal Age Pension': 'the Age Pension' }[l] || 'one goal')).join(' and ')}, but {sa.warning.replace(/^./, (c) => c.toLowerCase())}{' '}{link}</p>}
                    renderCard={() => card(sa.strategy)}
                />
            ))}

            {waysOn}

            {comparing && (
                <DialogShell titleId="rv-compare-title" title="The plans side by side" eyebrow={`Over about ${yrs}`} onClose={() => setComparing(false)} size="xl" testId="rv-compare-dialog" flush>
                    <div className="overflow-x-auto">
                        <table className="min-w-full text-[14px]">
                            <thead>
                                <tr className="border-b border-slate-200 text-left text-[12px] font-semibold text-slate-500">
                                    <th className="px-5 py-3"> </th>
                                    {base && <th className="px-5 py-3">Staying put</th>}
                                    {shown.map((s) => <th key={String(s.id)} className="px-5 py-3">{rvPlanWords(s, factFind, entryFee).title}</th>)}
                                </tr>
                            </thead>
                            <tbody className="tabular-nums">
                                {[
                                    { label: 'Left to the family', pick: legacyOf },
                                    { label: 'Age Pension, a year', pick: pensionOf },
                                    { label: 'Left over each year', pick: cashOf },
                                ].map((r) => (
                                    <tr key={r.label} className="border-b border-slate-100">
                                        <th className="px-5 py-3 text-left font-semibold text-slate-700">{r.label}</th>
                                        {base && <td className="px-5 py-3">{money(r.pick(base))}</td>}
                                        {shown.map((s) => <td key={String(s.id)} className="px-5 py-3 font-semibold text-slate-900">{money(r.pick(s))}</td>)}
                                    </tr>
                                ))}
                                <tr className="border-b border-slate-100">
                                    <th className="px-5 py-3 text-left font-semibold text-slate-700">Village fees, a year</th>
                                    {base && <td className="px-5 py-3">—</td>}
                                    {shown.map((s) => <td key={String(s.id)} className="px-5 py-3">{money(annualOngoing)}</td>)}
                                </tr>
                                <tr>
                                    <th className="px-5 py-3 text-left font-semibold text-slate-700">Paid into the village</th>
                                    {base && <td className="px-5 py-3">—</td>}
                                    {shown.map((s) => <td key={String(s.id)} className="px-5 py-3">{money(entryFee)}</td>)}
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </DialogShell>
            )}

            {why && (
                <DialogShell
                    titleId="rv-why-title"
                    eyebrow={rvPlanWords(why.row, factFind, entryFee).title}
                    title={why.page === 'pension' ? 'Why the Age Pension is what it is' : why.page === 'cashflow' ? 'Left over each year' : 'The village fees'}
                    onClose={() => setWhy(null)}
                    testId="rv-why-dialog"
                >
                    {why.page === 'pension' && (
                        <div className="space-y-3 text-[14px] leading-relaxed text-slate-700">
                            <p>Staying put: <strong>{money(pensionOf(base))} a year</strong>. With this plan: <strong>{money(pensionOf(why.row))} a year</strong> in the first year.{pensionReason(why.row) ? <> The difference is mostly because {pensionReason(why.row)}.</> : null}</p>
                            {(() => {
                                // The plan's own first-year count, in plain words — not step 2's "today" panel, whose
                                // "room to move" examples are not advice to give on a plan.
                                const f = why.row.projections?.[0]?.financials as FinancialPosition | undefined;
                                const pos = readMeansTestPosition((f as { socialSecurityBreakdown?: MeansTestBreakdownRow[] } | undefined)?.socialSecurityBreakdown, (f as { socialSecurity?: number } | undefined)?.socialSecurity, factFind?.adviceFor === 'couple');
                                if (!pos) return null;
                                return (
                                    <p data-testid="rv-why-pension-count">
                                        With this plan Centrelink counts <strong>{money(pos.assessableAssets)}</strong> of assets and <strong>{money(pos.assessableIncome)} a year</strong> of income.
                                        {' '}The {pos.determinedBy === 'income' ? 'income' : 'assets'} test sets the pension: it gives {money(pos.determinedBy === 'income' ? pos.incomeTestPension : pos.assetTestPension)} a year, against {money(pos.determinedBy === 'income' ? pos.assetTestPension : pos.incomeTestPension)} on the other test, and the lower of the two is paid.
                                    </p>
                                );
                            })()}
                        </div>
                    )}
                    {why.page === 'cashflow' && (
                        <div className="space-y-3 text-[14px] leading-relaxed text-slate-700">
                            <p>Money in, less money out, in the first year: <strong>{cashOf(why.row) < 0 ? `short ${money(-cashOf(why.row))}` : `${money(cashOf(why.row))} to spare`}</strong>. Staying put: {cashOf(base) < 0 ? `short ${money(-cashOf(base))}` : `${money(cashOf(base))} to spare`}.</p>
                            <p>The village fees of {money(annualOngoing)} a year are part of the money out. {(why.row.inputs as Record<string, unknown> | undefined)?.rentHome === true ? 'The rent from the home, after its costs and the weeks it sits empty, is part of the money in.' : (why.row.inputs as Record<string, unknown> | undefined)?.fundFrom === 'sell_home' ? 'What the sale money earns, wherever it is put, is part of the money in.' : 'What the savings left after the entry payment earn is part of the money in.'}</p>
                            <p>Explain this sets out every line of it, year by year.</p>
                        </div>
                    )}
                    {why.page === 'fees' && (
                        <div className="space-y-3 text-[14px] leading-relaxed text-slate-700">
                            <p>The village charges <strong>{money(annualOngoing)} a year</strong> for running the village — the same in every way of paying for the unit.</p>
                            <p>When {v.they} leave, the village keeps a departure fee from the entry payment: about <strong>{money(dmf)}</strong> after {yrs} on this contract. It is already counted in what each plan leaves the family.</p>
                        </div>
                    )}
                </DialogShell>
            )}
        </div>
    );
};

export default RvPlansView;
